// Container for the new Check-ins experience. Renders inside the app layout
// (the sidebar comes from the layout); owns the header bar, the team selector,
// screen state, data fetching, and the per-screen wiring.
import React, { useEffect, useState, useCallback } from 'react';
import { message } from 'antd';
import * as api from './checkinsApi';
import { getSlackChannels } from '../../workspace-teams/teamsApi';
import { TYPES, typeByKey } from './parts';
import CheckinsList from './CheckinsList';
import StandupReport from './StandupReport';
import StandupConfig from './StandupConfig';
import GenericConfig from './GenericConfig';
import './checkins.css';

const HEADERS = {
  list: ['Check-ins', 'Manage your team rituals'],
  report: ['Check-ins', 'Async team rituals, run in Slack'],
  'config-standup': ['Configure check-in', 'Automated Standup'],
  'config-generic': ['Configure check-in', ''],
};

function scheduleLabel(ts) {
  if (!ts) return null;
  if (ts.standupscheduleType === 'manual' || ts.jobtype === 'manual') return 'On demand';
  if (ts.time_at) {
    const days = (ts.weekdays || '').split(',').filter(Boolean);
    const span = days.length === 5 ? 'Weekdays' : days.length ? `${days.length} days` : 'Daily';
    return `${span}, ${ts.time_at}`;
  }
  return 'On demand';
}

function deriveRows(teamsyncs) {
  return TYPES.map((type) => {
    const ts = (teamsyncs || []).find((t) =>
      type.key === 'standup'
        ? t.engine === 'ai' || t.standuptype === 'ai_standup'
        : t.standuptype === type.standuptype
    );
    return {
      type: type.key,
      tId: ts && ts._id,
      enabled: !!(ts && ts.createInstance),
      schedule: scheduleLabel(ts),
      postsTo:
        ts && (ts.selectedChannel || (ts.reportChannels && ts.reportChannels[0]))
          ? '#' + ((ts.reportChannels && ts.reportChannels[0] && ts.reportChannels[0].name) || 'channel')
          : 'Not set',
      lastRun: (ts && ts.lastRunLabel) || 'Never',
      raw: ts,
    };
  });
}

function deriveLegacy(teamsyncs) {
  const known = new Set(TYPES.map((t) => t.standuptype).concat(['ai_standup']));
  return (teamsyncs || [])
    .filter((t) => t.engine !== 'ai' && !isCanonical(t, teamsyncs))
    .map((t) => ({
      id: t._id,
      name: t.name,
      state: t.createInstance ? 'active' : t.standupscheduleType === 'manual' ? 'paused' : 'disabled',
      owner: (t.user_id && (t.user_id.name || t.user_id.real_name)) || '—',
      participants: (t.selectedMembers && t.selectedMembers.length) || 0,
      reportingTo:
        (t.reportChannels && t.reportChannels[0] && '#' + t.reportChannels[0].name) ||
        (t.selectedChannel && '#channel') ||
        '—',
    }));
}

// A teamsync is "canonical" (shown as a tile) if it's the first of its type.
function isCanonical(ts, all) {
  const sameType = (all || []).filter((t) => t.standuptype === ts.standuptype);
  return sameType[0] && String(sameType[0]._id) === String(ts._id);
}

export default function CheckinsApp(props) {
  const wId = props.match.params.wId;
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState(localStorage.getItem('currentTeamId') || null);
  const [screen, setScreen] = useState('report');
  const [activeType, setActiveType] = useState('standup');
  const [teamsyncs, setTeamsyncs] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [saving, setSaving] = useState(false);

  const currentTeam = teams.find((t) => String(t._id) === String(teamId));
  const isDefault = !!(currentTeam && currentTeam.is_default);

  // Load teams once.
  useEffect(() => {
    api
      .listTeams(wId)
      .then((res) => {
        const list = (res.data && res.data.teams) || [];
        setTeams(list);
        if (!teamId && list.length) {
          const def = list.find((t) => !t.is_default) || list[0];
          setTeamId(String(def._id));
          localStorage.setItem('currentTeamId', String(def._id));
        }
      })
      .catch(() => {});
    getSlackChannels(wId)
      .then((res) => setChannels((res.data && (res.data.channels || res.data)) || []))
      .catch(() => {});
  }, [wId]);

  const loadList = useCallback(() => {
    if (!teamId) return;
    api
      .listForTeam(wId, teamId, isDefault)
      .then((res) => setTeamsyncs((res.data && res.data.teamSyncs) || []))
      .catch(() => setTeamsyncs([]));
  }, [wId, teamId, isDefault]);

  const loadReport = useCallback(() => {
    if (!teamId) return;
    setLoading(true);
    api
      .previewStandup(wId, teamId)
      .then((res) => setReport((res.data && res.data.report) || null))
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [wId, teamId]);

  useEffect(() => {
    if (!teamId) return;
    loadList();
    if (screen === 'report') loadReport();
  }, [teamId]);

  // The team switcher lives in the app sidebar; it only writes
  // localStorage.currentTeamId (no reload). Pick up its changes here so this
  // page stays in sync without a second switcher of our own.
  useEffect(() => {
    const sync = () => {
      const id = localStorage.getItem('currentTeamId');
      if (id && String(id) !== String(teamId)) {
        setTeamId(String(id));
        setScreen('report');
      }
    };
    window.addEventListener('focus', sync);
    const iv = setInterval(sync, 1500);
    return () => {
      window.removeEventListener('focus', sync);
      clearInterval(iv);
    };
  }, [teamId]);

  const onToggle = async (type, enabled) => {
    const row = deriveRows(teamsyncs).find((r) => r.type === type.key);
    if (!row || !row.tId) {
      message.info(`${type.name} isn't set up for this team yet — Configure it first.`);
      return;
    }
    try {
      await api.setEnabled(wId, row.tId, enabled);
      loadList();
    } catch (e) {
      message.error('Could not update status');
    }
  };

  const onConfigure = (type) => {
    setActiveType(type.key);
    setScreen(type.key === 'standup' ? 'config-standup' : 'config-generic');
  };

  const participantsFromReport = () =>
    (report && report.entries ? report.entries : []).map((e) => ({
      id: e.user_id || e.slack_user_id || e.name,
      name: e.name,
      area: e.area,
      included: true,
    }));

  const standupConfigFromRow = () => {
    const row = deriveRows(teamsyncs).find((r) => r.type === 'standup');
    const ts = row && row.raw;
    const ai = (ts && ts.ai_config) || {};
    return {
      enabled: !!(ts && ts.createInstance),
      time: (ts && ts.time_at) || '08:00 AM',
      timezone: (ts && ts.timezone) || 'Team default',
      channel: ts && ts.selectedChannel,
      readFrom: ai.read_from || ['jira', 'github', 'slack'],
      askDirectly: ai.ask_when_unsure === false ? 'never' : 'unsure',
      keepWeb: true,
    };
  };

  const saveStandup = async (form) => {
    setSaving(true);
    const dayMap = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
    const dayNums = (form.days || []).map((d) => dayMap[d]).sort();
    const chan = channels.find((c) => c.id === form.channel);
    try {
      await api.saveAiStandup(wId, {
        engine: 'ai',
        standuptype: 'ai_standup',
        name: 'Automated Standup',
        standupscheduleType: 'recurring',
        createInstance: form.active,
        workspace_team_id: teamId,
        time_at: form.time,
        weekdays: dayNums.join(','),
        days: dayNums,
        timezone_type: 'custom_timezone',
        timezone: form.timezone,
        selectedChannel: form.channel,
        reportChannels: chan ? [{ id: chan.id, name: chan.name }] : [],
        report_to: 'channel',
        report_type: 'after_wait_time',
        wait_time: 5400,
        selectedMembers: [],
        questions: [],
        ai_config: {
          review_before_post: true,
          ask_when_unsure: form.askDirectly !== 'never',
          read_from: form.readFrom,
        },
      });
      message.success('Automated Standup saved');
      loadList();
      setScreen('list');
    } catch (e) {
      message.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  const saveGeneric = async (form) => {
    const type = typeByKey(activeType);
    const row = deriveRows(teamsyncs).find((r) => r.type === activeType);
    setSaving(true);
    try {
      if (row && row.tId) {
        await api.updateCheckin(wId, row.tId, {
          createInstance: form.active,
          selectedChannel: form.channel,
          standuptype: type.standuptype,
        });
      } else {
        await api.saveAiStandup(wId, {
          engine: 'manual',
          standuptype: type.standuptype,
          name: type.name,
          standupscheduleType: form.cadence === 'on_demand' ? 'manual' : 'recurring',
          createInstance: form.active,
          workspace_team_id: teamId,
          selectedChannel: form.channel,
          questions: form.questions || [],
        });
      }
      message.success(`${type.name} saved`);
      loadList();
      setScreen('list');
    } catch (e) {
      message.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  const [title, subtitle] = (() => {
    const h = HEADERS[screen] || HEADERS.list;
    if (screen === 'config-generic') return [h[0], typeByKey(activeType).name];
    return h;
  })();

  const rows = deriveRows(teamsyncs);
  const legacy = deriveLegacy(teamsyncs);

  return (
    <div className="ck21">
      <div className="ck21__topbar">
        <div>
          <h1 className="ck21__title">{title}</h1>
          {subtitle ? <p className="ck21__subtitle">{subtitle}</p> : null}
        </div>
        {currentTeam ? (
          <div className="ck21__teamlabel">
            Team · <b>{currentTeam.name}</b>
            {currentTeam.is_default ? ' (General)' : ''}
          </div>
        ) : null}
      </div>

      <div className="ck21__page">
        {screen === 'list' && (
          <CheckinsList
            rows={rows}
            legacy={legacy}
            isDefault={isDefault}
            onConfigure={onConfigure}
            onToggle={onToggle}
            onBackToReport={() => {
              setScreen('report');
              loadReport();
            }}
          />
        )}

        {screen === 'report' && (
          <StandupReport
            report={report}
            loading={loading}
            dateLabel={new Date().toLocaleDateString(undefined, {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            })}
            onPrev={() => {}}
            onNext={() => {}}
            onRerun={loadReport}
            onViewSlack={() => {}}
            onEdit={() => {
              setActiveType('standup');
              setScreen('config-standup');
            }}
            onAllCheckins={() => setScreen('list')}
          />
        )}

        {screen === 'config-standup' && (
          <StandupConfig
            config={standupConfigFromRow()}
            channels={channels}
            participants={participantsFromReport()}
            saving={saving}
            onSave={saveStandup}
            onBack={() => setScreen('list')}
          />
        )}

        {screen === 'config-generic' && (
          <GenericConfig
            type={typeByKey(activeType)}
            config={(rows.find((r) => r.type === activeType) || {}).raw || {}}
            channels={channels}
            saving={saving}
            onSave={saveGeneric}
            onBack={() => setScreen('list')}
          />
        )}
      </div>
    </div>
  );
}
